import React, { useState, useEffect } from 'react';
import { supabase } from '../../../supabase';
import styles from './Lineup.module.css';
import Modal from '../../../components/admin/FormModal';
import Input from '../../../components/admin/Input';
import { RadioGroup } from '../../../components/common/Radio';
import { Checkbox } from '../../../components/common/Checkbox';
import AdminList from '../../../components/admin/AdminList';
import Layout from '../../../components/admin/Layout';
import IconTextButton from '../../../components/admin/IconTextButton';
import { Helmet } from 'react-helmet';
import TeamAssignment from './components/TeamAssignment';
import Button from '../../../components/common/Button';
import BulkAddPlayerForm from './components/BulkAddPlayerForm';
import Toast from '../../../components/common/Toast';
import LineupTalkSender from './components/MessageSender';

const TABLE_HEADERS = [
  { key: 'match_id', label: '매치ID', width: '100px' },
  { key: 'links', label: '바로가기', width: '200px' },
  { key: 'schedule', label: '일정', width: '180px' },
  { key: 'stadium', label: '구장', width: '150px' },
  { key: 'player_count', label: '참가자', width: '80px' },
];

const formatDateTime = (schedule) => {
  if (!schedule) return '';
  const date = new Date(schedule);
  return date.toLocaleString('ko-KR', {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    hour12: false
  });
};

const TEAMS = [
  { id: 'blue', name: '블루', color: '#2563eb', value: '블루' },
  { id: 'red', name: '레드', color: '#dc2626', value: '레드' },
  { id: 'yellow', name: '옐로우', color: '#ca8a04', value: '옐로우' },
  { id: 'unassigned', name: '미배정', color: '#6b7280', value: null },
];

// 참가자 목록 헤더 정의
const PLAYER_LIST_HEADERS = [
  { label: '팀', key: 'color' },
  { label: '번호', key: 'number' },
  { label: '이름', key: 'name' },
  { label: '레벨', key: 'level' },
  { label: '성별', key: 'sex' },
  { label: '나이', key: 'age' },
  { label: '리드', key: 'lead' },
  { label: '지각', key: 'late' }
];

function AdminLineup() {
  const [matches, setMatches] = useState([]);
  const [users, setUsers] = useState([]);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingMatch, setEditingMatch] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [isBulkAdding, setIsBulkAdding] = useState(false);
  const [toast, setToast] = useState({ show: false, message: '', type: 'info' });
  const [successMessage, setSuccessMessage] = useState('');
  const [errorMessage, setErrorMessage] = useState('');

  useEffect(() => {
    fetchMatches();
    fetchUsers();
  }, []);

  const fetchMatches = async () => {
    try {
      setIsLoading(true);
      
      const { data, error } = await supabase
        .from('lineup_match')
        .select(`
          id,
          match_id,
          schedule,
          stadium,
          play_time,
          created_at,
          lineup_match_player (
            id
          )
        `)
        .order('schedule', { ascending: false });
        
      if (error) throw error;
      
      const transformedData = transformMatchData(data);
      setMatches(transformedData);
      
      // 첫 번째 매치를 기본으로 선택
      if (transformedData.length > 0 && !editingMatch) {
        setEditingMatch(transformedData[0]);
      }
    } catch (error) {
      console.error('Error fetching matches:', error);
      setError(error.message);
    } finally {
      setIsLoading(false);
    }
  };

  const fetchUsers = async () => {
    try {
      const { data, error } = await supabase
        .from('lineup_user')
        .select('*')
        .order('name');
      
      if (error) throw error;
      setUsers(data);
    } catch (err) {
      console.error('Error fetching users:', err);
    }
  };

  const handleOpenModal = (match = null) => {
    console.log('Opening modal with match:', match);  // 디버깅용 로그
    
    if (match && match.id) {
      // 매치 상세 정보 다시 로드
      fetchMatchDetails(match.id);
    } else {
      // 새 매치 추가 시 빈 객체로 초기화
      setEditingMatch({
        match_id: '',
        schedule: '',
        stadium: '',
        stadium_memo: '',
        manager_name: '',
        manager_phone: '',
        play_time: '',
        lineup_match_player: []
      });
      setIsModalOpen(true);
    }
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setEditingMatch(null);
  };

  const showToast = (message, type = 'info') => {
    setToast({ show: true, message, type });
    setTimeout(() => {
      setToast({ show: false, message: '', type: 'info' });
    }, 3000);
  };

  const handleFormSubmit = async (e) => {
    e.preventDefault();
    
    if (!editingMatch) return;
    
    try {
      setIsLoading(true);
      
      // 매치 데이터 준비
      const matchData = {
        schedule: editingMatch.schedule,
        stadium: editingMatch.stadium,
        stadium_memo: editingMatch.stadium_memo,
        manager_name: editingMatch.manager_name,
        manager_phone: editingMatch.manager_phone,
        play_time: editingMatch.play_time,
        player_per_team: editingMatch.player_per_team,
        is_auto_rotation: editingMatch.is_auto_rotation,
        is_plaber_match: editingMatch.is_plaber_match
      };
      
      // 새 매치 생성 시에는 match_id 포함
      if (!editingMatch?.id) {
        matchData.match_id = editingMatch?.match_id || null;
      }

      console.log('Saving match data:', matchData);  // 디버깅용 로그

      if (editingMatch?.id) {
        // 기존 매치 수정 (match_id 제외)
        const { error } = await supabase
          .from('lineup_match')
          .update(matchData)
          .eq('id', editingMatch.id);

        if (error) throw error;
        
        // match_id 변경 처리 - 원본 매치 데이터와 현재 편집 중인 매치 데이터의 match_id가 다를 때만 실행
        const originalMatch = matches.find(m => m.id === editingMatch.id);
        if (originalMatch && originalMatch.match_id !== editingMatch.match_id) {
          try {
            // 1. 현재 매치의 선수 데이터 가져오기
            const { data: playerData, error: playerError } = await supabase
              .from('lineup_match_player')
              .select('id')
              .eq('match_id', editingMatch.id);
              
            if (playerError) throw playerError;
            
            // 2. 선수 데이터가 있는지 확인
            if (playerData && playerData.length > 0) {
              // 선수가 등록된 경우 match_id 수정 불가
              console.log(`${playerData.length}명의 선수가 등록되어 있어 match_id를 수정할 수 없습니다.`);
              showToast(`등록된 선수가 있어요. 선수를 제거한 후 수정할 수 있어요`, 'warning');
            } else {
              // 선수 데이터가 없는 경우 match_id 업데이트
              const { error: matchIdError } = await supabase
                .from('lineup_match')
                .update({ match_id: editingMatch.match_id })
                .eq('id', editingMatch.id);
                
              if (matchIdError) throw matchIdError;
              
              console.log('매치 ID 업데이트 완료');
            }
          } catch (updateError) {
            console.error('match_id 업데이트 중 오류:', updateError);
            throw new Error(`매치 ID 업데이트 실패: ${updateError.message}`);
          }
        }
      } else {
        // 새 매치 생성
        const { error } = await supabase
          .from('lineup_match')
          .insert(matchData);

        if (error) throw error;
      }

      await fetchMatches(); // 목록 새로고침
      handleCloseModal();
    } catch (error) {
      console.error('Error saving match:', error);
      alert('매치 저장 중 오류가 발생했습니다: ' + error.message);
    } finally {
      setIsLoading(false);
    }
  };

  const handleTeamChange = React.useCallback(async (playerId, newTeamId) => {
    try {
      // 1. 현재 선수 정보 가져오기
      const player = editingMatch.lineup_match_player.find(p => p.id === playerId);
      if (!player) {
        console.error('선수를 찾을 수 없습니다:', playerId);
        return;
      }

      // 2. 이전 팀 정보 저장
      const oldTeamName = player.team_name;

      // 3. 새로운 팀 정보 가져오기
      const team = TEAMS.find(t => t.id === newTeamId);
      const newTeamName = team ? team.value : null;

      // 4. 해당 팀의 현재 플레이어들 가져오기 (순서 계산용)
      const newTeamPlayers = editingMatch.lineup_match_player.filter(p => 
        newTeamId === 'unassigned' ? !p.team_name : p.team_name === team.value
      );

      // 5. 새로운 팀 번호 계산 (팀의 마지막 번호 + 1)
      const newTeamNumber = newTeamPlayers.length + 1;

      // 6. 데이터베이스 업데이트 - 선수의 팀 변경
      const { error } = await supabase
        .from('lineup_match_player')
        .update({
          team_name: newTeamName,
          team_number: newTeamName ? newTeamNumber : null // 미배정인 경우 null
        })
        .eq('id', playerId);

      if (error) throw error;

      // 7. 로컬 상태 업데이트 - 선수의 팀 변경
      const updatedPlayers = editingMatch.lineup_match_player.map(p => {
        if (p.id === playerId) {
          return {
            ...p,
            team_name: newTeamName,
            team_number: newTeamName ? newTeamNumber : null
          };
        }
        return p;
      });

      // 8. 이전 팀의 선수들 등번호 재정렬 (선수가 팀에서 빠진 경우)
      if (oldTeamName && oldTeamName !== newTeamName) {
        // 이전 팀의 선수들 가져오기
        const oldTeamPlayers = updatedPlayers.filter(p => p.team_name === oldTeamName);
        
        // 등번호 순으로 정렬
        oldTeamPlayers.sort((a, b) => (a.team_number || 0) - (b.team_number || 0));
        
        // 등번호 재할당
        const updates = oldTeamPlayers.map((player, index) => ({
          id: player.id,
          team_number: index + 1
        }));
        
        // DB 업데이트
        for (const update of updates) {
          const { error: updateError } = await supabase
            .from('lineup_match_player')
            .update({ team_number: update.team_number })
            .eq('id', update.id);
          
          if (updateError) {
            console.error('선수 등번호 업데이트 오류:', updateError);
            throw updateError;
          }
        }
        
        // 로컬 상태 업데이트 - 이전 팀 선수들의 등번호 변경
        updatedPlayers.forEach(player => {
          const update = updates.find(u => u.id === player.id);
          if (update) {
            player.team_number = update.team_number;
          }
        });
      }

      // 9. 최종 상태 업데이트
      setEditingMatch(prev => ({
        ...prev,
        lineup_match_player: updatedPlayers
      }));

    } catch (error) {
      console.error('Error updating team:', error);
      alert('팀 변경 중 오류가 발생했습니다.');
    }
  }, [editingMatch]);

  const handleAddPlayer = React.useCallback(async (userId) => {
    if (!userId || !editingMatch) return;

    try {
      console.log('Adding player with userId:', userId, 'to match:', editingMatch.id);
      
      // 데이터베이스 구조 확인을 위한 로그
      console.log('Current match data:', editingMatch);
      
      // 1. 새로운 플레이어 추가
      // lineup_match_player 테이블의 match_id는 lineup_match 테이블의 id를 참조
      const { data, error } = await supabase
        .from('lineup_match_player')
        .insert({
          match_id: editingMatch.match_id,  // lineup_match 테이블의 id 필드 참조
          lineup_user_id: userId,     // lineup_user 테이블의 id 필드 참조
          team_name: null,
          team_number: null
        })
        .select(`
          id,
          team_name,
          team_number,
          is_lead,
          is_late,
          lineup_user (
            id,
            name,
            level,
            phone,
            sex
          )
        `)
        .single();

      if (error) {
        console.error('Supabase error:', error);
        throw error;
      }

      console.log('Added player data:', data);

      // 2. 로컬 상태 업데이트
      setEditingMatch(prev => ({
        ...prev,
        lineup_match_player: [...(prev.lineup_match_player || []), data]
      }));

    } catch (error) {
      console.error('Error adding player:', error);
      alert('참가자 추가 중 오류가 발생했습니다: ' + error.message);
    }
  }, [editingMatch]);

  const handleBulkAddPlayers = async (players) => {
    try {
      setIsLoading(true);
      setError(null);

      console.log('Bulk adding players:', players); // 디버깅용

      // 1. 모든 유저 생성
      const { data: newUsers, error: usersError } = await supabase
        .from('lineup_user')
        .insert(
          players.map(player => ({
            name: player.name,
            level: player.level,
            phone: player.phone,
            sex: player.sex,
            manner: 50,
            play_style: [],
            created_at: new Date().toISOString()
          }))
        )
        .select();

      if (usersError) {
        console.error('Error creating users:', usersError);
        throw usersError;
      }

      console.log('Created users:', newUsers); // 디버깅용

      // 2. 매치에 모든 유저 추가 (배열로 한 번에 추가)
      if (editingMatch?.match_id && newUsers?.length > 0) {
        const matchPlayers = newUsers.map(user => ({
          lineup_user_id: user.id,
          match_id: editingMatch.match_id,
          team_name: null,
          created_at: new Date().toISOString()
        }));

        console.log('Adding match players:', matchPlayers); // 디버깅용

        const { data: newMatchPlayers, error: matchPlayersError } = await supabase
          .from('lineup_match_player')
          .insert(matchPlayers)
          .select(`
            id,
            lineup_user_id,
            team_name,
            team_number,
            is_lead,
            is_late,
            lineup_user (
              id,
              name,
              level,
              sex,
              manner
            )
          `);

        if (matchPlayersError) {
          console.error('Error creating match players:', matchPlayersError);
          throw matchPlayersError;
        }

        console.log('Added match players:', newMatchPlayers); // 디버깅용

        // 로컬 상태 업데이트
        setEditingMatch(prev => {
          const currentPlayers = prev.lineup_match_player || [];
          return {
            ...prev,
            lineup_match_player: [...currentPlayers, ...newMatchPlayers]
          };
        });
      }

      await fetchUsers();
      setIsBulkAdding(false);
      alert(`${newUsers.length}명의 참가자가 성공적으로 추가되었습니다.`);
    } catch (error) {
      console.error('Error in bulk adding players:', error);
      setError(error.message);
      alert(`참가자 일괄 추가 중 오류가 발생했습니다: ${error.message}`);
    } finally {
      setIsLoading(false);
    }
  };

  const handleMove = async (teamId, updates) => {
    try {
      // 현재 선수 목록 복사
      const updatedPlayers = [...editingMatch.lineup_match_player];
      
      // 업데이트할 선수들의 등번호 변경
      updates.forEach(update => {
        const playerIndex = updatedPlayers.findIndex(p => p.id === update.id);
        if (playerIndex !== -1) {
          updatedPlayers[playerIndex] = {
            ...updatedPlayers[playerIndex],
            team_number: update.team_number
          };
        }
      });
      
      // 상태 업데이트 (UI 즉시 반영)
      setEditingMatch(prev => ({
        ...prev,
        lineup_match_player: updatedPlayers
      }));
      
      // DB 업데이트
      for (const update of updates) {
        const { error } = await supabase
          .from('lineup_match_player')
          .update({ team_number: update.team_number })
          .eq('id', update.id);
        
        if (error) {
          console.error('선수 등번호 업데이트 오류:', error);
          throw error;
        }
      }
      
      // 성공 메시지 표시
      setSuccessMessage('선수 순서가 업데이트되었습니다.');
      setTimeout(() => setSuccessMessage(''), 3000);
    } catch (error) {
      console.error('선수 이동 오류:', error);
      setErrorMessage('선수 순서 업데이트 중 오류가 발생했습니다.');
      setTimeout(() => setErrorMessage(''), 3000);
    }
  };

  const handleRemovePlayer = React.useCallback(async (playerId) => {
    try {
      // 1. 데이터베이스에서 플레이어 제거
      const { error } = await supabase
        .from('lineup_match_player')
        .delete()
        .eq('id', playerId);

      if (error) throw error;

      // 2. 로컬 상태 업데이트
      setEditingMatch(prev => ({
        ...prev,
        lineup_match_player: prev.lineup_match_player.filter(
          player => player.id !== playerId
        )
      }));

    } catch (error) {
      console.error('Error removing player:', error);
      alert('참가자 제거 중 오류가 발생했습니다.');
    }
  }, []);

  const fetchMatchDetails = async (matchId) => {
    try {
      setIsLoading(true);
      
      const { data, error } = await supabase
        .from('lineup_match')
        .select(`
          id,
          match_id,
          schedule,
          stadium,
          stadium_memo,
          manager_name,
          manager_phone,
          play_time,
          player_per_team,
          is_auto_rotation,
          is_plaber_match,
          lineup_match_player (
            id,
            team_name,
            team_number,
            is_lead,
            is_late,
            lineup_user (
              id,
              name,
              level,
              phone,
              sex
            )
          )
        `)
        .eq('id', matchId)
        .single();
        
      if (error) throw error;
      
      setEditingMatch(data);
      setIsModalOpen(true);
    } catch (error) {
      console.error('Error fetching match details:', error);
      alert('매치 정보를 불러오는 중 오류가 발생했습니다.');
    } finally {
      setIsLoading(false);
    }
  };

  const transformMatchData = (matches) => {
    return matches.map(match => {
      const playerCount = match.lineup_match_player ? match.lineup_match_player.length : 0;
      const formattedDate = match.schedule ? formatDateTime(match.schedule) : '';
      
      return {
        ...match,
        player_count: playerCount,
        schedule: formattedDate,
        links: (
          <div className={styles.linkButtons}>
            <a 
              href={`https://plabfootball.com/match/${match.match_id}/`} 
              target="_blank" 
              rel="noopener noreferrer"
              className={styles.linkButton}
            >
              매치
            </a>
            <a 
              href={`https://www.plabfootball.com/e-to-play/payment/matchforapply/${match.match_id}/change/`} 
              target="_blank" 
              rel="noopener noreferrer"
              className={styles.linkButton}
            >
              어드민
            </a>
            <a 
              href={`https://plabfootball.com/report/match/${match.match_id}/`} 
              target="_blank" 
              rel="noopener noreferrer"
              className={styles.linkButton}
            >
              리포트
            </a>
            <a 
              href={`https://design.plabfootball.com/lineup/${match.match_id}/`} 
              target="_blank" 
              rel="noopener noreferrer"
              className={styles.linkButton}
            >
              라인업
            </a>
          </div>
        )
      };
    });
  };

  // 체크박스 변경 핸들러
  const handleCheckboxChange = async (playerId, field, value) => {
    try {
      const updateData = { [field]: value };
      const { error } = await supabase
        .from('lineup_match_player')
        .update(updateData)
        .eq('id', playerId);

      if (error) throw error;

      // 로컬 상태 업데이트
      setEditingMatch(prev => ({
        ...prev,
        lineup_match_player: prev.lineup_match_player.map(player =>
          player.id === playerId ? { ...player, ...updateData } : player
        )
      }));
    } catch (error) {
      console.error('Error updating player:', error);
      alert('업데이트 중 오류가 발생했습니다.');
    }
  };

  // 정렬된 데이터 준비
  const sortedPlayerData = React.useMemo(() => {
    if (!editingMatch?.lineup_match_player) return [];

    return [...editingMatch.lineup_match_player].sort((a, b) => {
      // 팀 이름 내림차순 정렬
      if (a.team_name < b.team_name) return 1;
      if (a.team_name > b.team_name) return -1;

      // 팀 번호 내림차순 정렬
      return (a.team_number || 0) - (b.team_number || 0);
    });
  }, [editingMatch?.lineup_match_player]);

  if (!matches || matches.length === 0) {
    return (
      <div className={styles.container}>
        <div className={styles.loadingContainer}>
          <div className={styles.loading}></div>
          <p>매치 데이터를 로딩 중입니다...</p>
        </div>
      </div>
    );
  }
  
  if (error) {
    return <div>Error: {error}</div>;
  }

  return (
    <Layout>
      <Helmet>
        <title>어드민:라인업</title>
      </Helmet>
      {isLoading && <div className={styles.loading}>Loading...</div>}
      {toast.show && (
        <Toast 
          message={toast.message} 
          type={toast.type} 
          onClose={() => setToast({ show: false, message: '', type: 'info' })} 
        />
      )}
      <div className={styles.toolBar}>
        <IconTextButton
          icon="icon_plus_square.svg"
          text="추가하기"
          onClick={() => handleOpenModal()}
          state="Enabled"
        />
      </div>
      <AdminList
        headers={TABLE_HEADERS}
        data={matches}
        onRowClick={handleOpenModal}
      />

      <Modal
        isOpen={isModalOpen}
        onClose={handleCloseModal}
        onSubmit={handleFormSubmit}
      >
        <div className={styles.section}>
            <h3>매치 정보</h3>
            <Input
              label="매치ID"
              type="text"
              name="match_id"
              value={editingMatch?.match_id || ''}
            onChange={(e) => {
              setEditingMatch(prev => ({
                ...prev,
                match_id: e.target.value
              }));
            }}
            required
          />
          <Input
            label="일정"
            type="datetime-local"
            name="schedule"
            value={editingMatch?.schedule || ''}
            onChange={(e) => {
              setEditingMatch(prev => ({
                ...prev,
                schedule: e.target.value
              }));
            }}
            required
          />
          <Input
            label="구장"
            type="text"
            name="stadium"
            value={editingMatch?.stadium || ''}
            onChange={(e) => {
              setEditingMatch(prev => ({
                ...prev,
                stadium: e.target.value
              }));
            }}
            required
          />
          <Input
            label="구장 메모"
            type="textarea"
            name="stadium_memo"
            value={editingMatch?.stadium_memo || ''}
            onChange={(e) => {
              setEditingMatch(prev => ({
                ...prev,
                stadium_memo: e.target.value
              }));
            }}
            rows="3"
          />
          <Input
            label="매니저 이름"
            type="text"
            name="manager_name"
            value={editingMatch?.manager_name || ''}
            onChange={(e) => {
              setEditingMatch(prev => ({
                ...prev,
                manager_name: e.target.value
              }));
            }}
          />
          <Input
            label="매니저 전화번호"
            type="tel"
            name="manager_phone"
            value={editingMatch?.manager_phone || ''}
            onChange={(e) => {
              setEditingMatch(prev => ({
                ...prev,
                manager_phone: e.target.value
              }));
            }}
          />
          <Input
            label="매치 시간(분)"
            type="text"
            name="play_time"
            value={editingMatch?.play_time || ''}
            onChange={(e) => {
              setEditingMatch(prev => ({
                ...prev,
                play_time: e.target.value
              }));
            }}
          />
          <div className={styles.rotation}>
              <RadioGroup
                  label="로테이션"
                  name="player_per_team"
                  options={[{label: '5vs5', value: '5vs5'}, {label: '6vs6', value: '6vs6'}]}
                  value={editingMatch?.player_per_team}
                  onChange={(e) => setEditingMatch({...editingMatch, player_per_team: e.target.value})}
              />
              <Checkbox
                  label="쿼터 순서 탭 표시하기"
                  name="is_auto_rotation"
                  checked={editingMatch?.is_auto_rotation === 1}
                  onChange={(e) => setEditingMatch({
                      ...editingMatch, 
                      is_auto_rotation: e.target.checked ? 1 : 0  
                  })}
              />
              <Checkbox
                  label="플래버 매치"
                  name="is_plaber_match"
                  checked={editingMatch?.is_plaber_match === 1}
                  onChange={(e) => setEditingMatch({
                      ...editingMatch, 
                      is_plaber_match: e.target.checked ? 1 : 0  
                  })}
              />
          </div>
        </div>

        
        <div className={styles.playerSection}>
          <h3>참가자 관리</h3>
          {isBulkAdding ? (
            <BulkAddPlayerForm
              onSubmit={handleBulkAddPlayers}
              onCancel={() => setIsBulkAdding(false)}
            />
          ) : (
            <div className={styles.playerControls}>
              <Input
                label="참가자 선택"
                type="select"
                name="new_player"
                value=""
                onChange={(e) => handleAddPlayer(e.target.value)}
                options={users.map(user => ({
                  id: user.id,
                  name: `${user.name} (Lv.${user.level})`
                }))}
              />
              <Button
                onClick={() => setIsBulkAdding(true)}
                variant="secondary"
              >
                일괄 등록
              </Button>
            </div>
          )}
        </div>
        <div className={styles.teamSection}>
          <div className={styles.sectionHeader}>
            <h3>팀 배정</h3>
          </div>
          
          {editingMatch?.lineup_match_player && (
            <TeamAssignment
              players={editingMatch.lineup_match_player}
              onTeamChange={handleTeamChange}
              onMove={handleMove}
              onRemove={handleRemovePlayer}
            />
          )}
        </div>
        
        {/* 라인업톡 발송 컴포넌트 */}
        <LineupTalkSender 
          match={editingMatch} 
          onSuccess={(message) => showToast(message, 'success')}
          onError={(message) => showToast(message, 'error')}
        />
        <div className={styles.section}>
          <h3>참가자 목록</h3>
          {sortedPlayerData.length > 0 ? (
            <AdminList
              headers={PLAYER_LIST_HEADERS}
              data={sortedPlayerData.map(player => ({
                id: player.id,
                color: player.team_name || '-',
                number: player.team_number || '-',
                name: player.lineup_user?.name || '-',
                level: player.lineup_user?.level || '-',
                sex: player.lineup_user?.sex || '-',
                age: player.lineup_user?.age || '-',
                lead: (
                  <input
                    type="checkbox"
                    checked={player.is_lead === 1}
                    onChange={(e) => handleCheckboxChange(player.id, 'is_lead', e.target.checked ? 1 : 0)}
                  />
                ),
                late: (
                  <input
                    type="checkbox"
                    checked={player.is_late === 1}
                    onChange={(e) => handleCheckboxChange(player.id, 'is_late', e.target.checked ? 1 : 0)}
                  />
                )
              }))}
              onRowClick={() => {}} // 행 클릭 시 동작 없음
            />
          ) : (
            <div className={styles.emptyState}>
              등록된 참가자가 없습니다.
            </div>
          )}
        </div>
      </Modal>
    </Layout>
  );
}

export default React.memo(AdminLineup); 